import * as Popover from '@rn-primitives/popover';
import { type Href } from 'expo-router';
import { type ReactNode, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, { FadeIn, FadeInUp, FadeOut, FadeOutUp } from 'react-native-reanimated';

import { Badge } from '@/components/ui/Badge';
import { Divider } from '@/components/ui/Divider';
import { Text } from '@/components/ui/Text';

import { MenuItem } from './MenuItem';

export interface AccountMenuProps {
  /** The trigger that opens the menu — typically the Home settings icon. */
  children: ReactNode;
  name: string;
  email: string;
  plan?: string;
  /** Settings route — renders the item as a real `<a href>` on web. */
  settingsHref: Href;
  /** Manage-subscription route; when set, shows the item as a real `<a href>`. */
  manageSubscriptionHref?: Href;
  onHelp?: () => void;
  onSignOut: () => void;
}

/** Account popover anchored to its trigger (the Home settings icon). */
export function AccountMenu({
  children,
  name,
  email,
  plan,
  settingsHref,
  manageSubscriptionHref,
  onHelp,
  onSignOut,
}: AccountMenuProps) {
  const triggerRef = useRef<Popover.TriggerRef>(null);

  // Close the popover before running the action, matching the old menu's
  // close-then-act behavior.
  const handle = (fn?: () => void) => () => {
    triggerRef.current?.close();
    fn?.();
  };

  return (
    <Popover.Root>
      <Popover.Trigger ref={triggerRef} asChild>
        {children}
      </Popover.Trigger>
      {/* Portal accepts a SINGLE child on web (Radix), so the dim backdrop and
          the anchored Content are SIBLINGS under one wrapper — Content paints on
          top, so backdrop taps close the menu while taps on the menu hit the
          menu. The dim only fades; the slide-on-Y lives on the Content alone so
          the full-screen tint never shifts and exposes an edge. */}
      <Popover.Portal>
        <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
          <Animated.View
            entering={FadeIn.duration(110)}
            exiting={FadeOut.duration(90)}
            className="absolute inset-0"
          >
            <Pressable
              className="flex-1 bg-black/20"
              onPress={() => triggerRef.current?.close()}
            />
          </Animated.View>
          {/* No `asChild`: Content's native positioning style injects
              `opacity: 0` for the first (pre-measure) frame. Letting Content
              render its own wrapper View absorbs that opacity so it doesn't
              collide with the Animated.View's fade layout animation (which
              Reanimated warns about). The slide/fade lives on the inner view. */}
          <Popover.Content side="bottom" align="end" sideOffset={6}>
            <Animated.View
              entering={FadeInUp.duration(110)}
              exiting={FadeOutUp.duration(90)}
              className="w-72 rounded-2xl border border-border bg-background p-2 shadow-lg"
            >
              <View className="px-2 py-2">
                <Text variant="bodySemibold" numberOfLines={1}>
                  {name}
                </Text>
                <Text variant="caption" numberOfLines={1}>
                  {email}
                </Text>
              </View>
              <Divider className="my-1" />
              <MenuItem label="Settings" href={settingsHref} onPress={handle()} />
              {manageSubscriptionHref ? (
                <MenuItem
                  label="Manage subscription"
                  href={manageSubscriptionHref}
                  right={plan ? <Badge label={plan} tone="neutral" /> : undefined}
                  onPress={handle()}
                />
              ) : null}
              {onHelp ? (
                <MenuItem label="Help & support" onPress={handle(onHelp)} />
              ) : null}
              <Divider className="my-1" />
              <MenuItem label="Sign out" danger onPress={handle(onSignOut)} />
            </Animated.View>
          </Popover.Content>
        </View>
      </Popover.Portal>
    </Popover.Root>
  );
}
